/**
 * React Query hooks for contributor asset-library management.
 *
 * Endpoints:
 *   GET    /contributor/arches/:archeId/asset-library/config
 *   POST   /contributor/arches/:archeId/asset-library/{enable,disable}
 *   GET    /contributor/arches/:archeId/billing/summary
 *   GET    /contributor/arches/:archeId/assets
 *   POST   /contributor/arches/:archeId/assets/upload-init
 *   POST   /contributor/arches/:archeId/assets/:assetId/finalize
 *   PATCH  /contributor/arches/:archeId/assets/:assetId
 *   POST   /contributor/arches/:archeId/assets/:assetId/soft-delete
 *   POST   /contributor/arches/:archeId/manifests/snapshot
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ContributorArcheAsset } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function useArcheAssetLibraryConfig(archeId: string) {
  return useQuery({
    queryKey: queryKeys.contributorArches.libraryConfig(archeId),
    queryFn: () => api.getArcheAssetLibraryConfig(archeId),
    enabled: !!archeId,
  });
}

export function useArcheAssetBillingSummary(archeId: string) {
  return useQuery({
    queryKey: queryKeys.contributorArches.billingSummary(archeId),
    queryFn: () => api.getArcheAssetBillingSummary(archeId),
    enabled: !!archeId,
  });
}

export function useArcheAssetsList(
  archeId: string,
  opts?: { status?: string; tag?: string; cursor?: string; limit?: number },
) {
  return useQuery({
    queryKey: queryKeys.contributorArches.assets(archeId, opts as Record<string, unknown>),
    queryFn: () => api.listArcheAssets(archeId, opts),
    enabled: !!archeId,
  });
}

export function useEnableArcheAssetLibrary(archeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.enableArcheAssetLibrary(archeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.contributorArches.libraryConfig(archeId) });
    },
  });
}

export function useDisableArcheAssetLibrary(archeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.disableArcheAssetLibrary(archeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.contributorArches.libraryConfig(archeId) });
    },
  });
}

/**
 * Push the current draft library live on an already-published arche.
 *
 * The plugin runtime reads the published MANIFEST, not the asset rows, so
 * an upload is invisible to a live arche until something snapshots it.
 * Publishing the arche does that automatically; this is the standalone
 * path for "I only added art".
 */
export function useSnapshotArcheAssetManifest(archeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.snapshotArcheAssetManifest(archeId, true),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.contributorArches.assets(archeId) });
      qc.invalidateQueries({ queryKey: queryKeys.contributorArches.billingSummary(archeId) });
    },
  });
}

export interface UploadProgress {
  file: File;
  status: 'pending' | 'uploading' | 'finalizing' | 'done' | 'error';
  errorMessage?: string;
  asset?: ContributorArcheAsset;
}

/**
 * Performs the 3-step upload (init → S3 PUT → finalize) for one
 * file. Returns the finalized asset row. Components track multi-
 * file batches by calling this once per file with their own
 * progress UI; per-batch concurrency is the caller's concern.
 */
export function useUploadArcheAsset(archeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: File; tags?: string[] }) => {
      const init = await api.initArcheAssetUpload(archeId, {
        contentType: input.file.type as 'image/png' | 'image/jpeg' | 'image/webp',
        sizeBytes: input.file.size,
        name: input.file.name,
        tags: input.tags,
      });

      // Direct S3 PUT — no auth header (URL is presigned).
      const putRes = await fetch(init.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': input.file.type,
        },
        body: input.file,
      });
      if (!putRes.ok) {
        throw new Error(`S3 upload failed (${putRes.status})`);
      }

      const fin = await api.finalizeArcheAsset(archeId, init.assetId, {
        name: input.file.name,
        tags: input.tags,
      });
      return fin.asset;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.contributorArches.assets(archeId) });
      qc.invalidateQueries({ queryKey: queryKeys.contributorArches.libraryConfig(archeId) });
    },
  });
}

export function usePatchArcheAsset(archeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      assetId: string;
      name?: string | null;
      altText?: string | null;
      tags?: string[];
    }) => api.patchArcheAsset(archeId, input.assetId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.contributorArches.assets(archeId) });
    },
  });
}

export function useSoftDeleteArcheAsset(archeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assetId: string) => api.softDeleteArcheAsset(archeId, assetId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.contributorArches.assets(archeId) });
      qc.invalidateQueries({ queryKey: queryKeys.contributorArches.libraryConfig(archeId) });
    },
  });
}
